import { useCallback, useEffect, useRef, useState } from 'react'

export const MEETING_WINDOW_NAME = 'relaii-meeting'

/** One view of the existing call. Media and membership stay in CallProvider. */
export function useMeetingWindow(callId: string | null, title: string) {
  const popupRef = useRef<Window | null>(null)
  const [target, setTarget] = useState<HTMLElement | null>(null)
  const [error, setError] = useState<string | null>(null)

  const close = useCallback(() => {
    const popup = popupRef.current
    popupRef.current = null
    setTarget(null)
    popup?.close()
  }, [])

  const open = useCallback(() => {
    if (callId === null) return
    const existing = popupRef.current
    if (existing !== null && !existing.closed) {
      existing.focus()
      return
    }
    // Must happen synchronously inside the click, before any await.
    const popup = window.open('about:blank', MEETING_WINDOW_NAME, 'popup,width=960,height=720')
    if (popup === null) {
      setError('The meeting window was blocked. Allow pop-up windows for this Space, then try again.')
      return
    }
    try {
      const doc = popup.document
      doc.title = `${title} · relaii`
      doc.documentElement.lang = document.documentElement.lang
      doc.documentElement.className = document.documentElement.className
      for (const attribute of Array.from(document.documentElement.attributes)) {
        if (attribute.name.startsWith('data-') || attribute.name === 'style') {
          doc.documentElement.setAttribute(attribute.name, attribute.value)
        }
      }
      for (const node of document.querySelectorAll<HTMLLinkElement | HTMLStyleElement>('link[rel="stylesheet"], style')) {
        const copy = node.cloneNode(true) as HTMLLinkElement | HTMLStyleElement
        if (node instanceof HTMLLinkElement) (copy as HTMLLinkElement).href = node.href
        doc.head.append(copy)
      }
      const host = doc.createElement('div')
      doc.body.append(host)
      popupRef.current = popup
      popup.addEventListener('pagehide', () => {
        if (popupRef.current !== popup) return
        popupRef.current = null
        setTarget(null)
      }, { once: true })
      setError(null)
      setTarget(host)
      popup.focus()
    } catch {
      popup.close()
      setError('The meeting window could not open. Your meeting is still here; try again.')
    }
  }, [callId, title])

  useEffect(() => {
    if (callId === null) close()
    // Replacing a call must also release the previous call's window.
    return close
  }, [callId, close])

  useEffect(() => {
    if (target === null) return
    const popup = popupRef.current
    if (popup === null) return
    popup.document.title = `${title} · relaii`
    // Native close/crash paths do not all dispatch pagehide.
    const timer = window.setInterval(() => {
      if (popup.closed && popupRef.current === popup) {
        popupRef.current = null
        setTarget(null)
      }
    }, 500)
    const closeWithParent = () => popup.close()
    window.addEventListener('pagehide', closeWithParent)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('pagehide', closeWithParent)
    }
  }, [target, title])

  return { target, error, open, close, dismissError: () => setError(null) }
}
